import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import {
  MoreVertical,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Droplets,
  Share2,
  Users,
  Wallet,
  CalendarDays,
  Trash2,
  IndianRupee,
} from 'lucide-react'
import LockAlbumModal from './LockAlbumModal'
import { Albums } from '../../../../Data/album.dto'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import { setCurrentAlbum } from '../../../../Redux/Slice/Dashboard/AlbumSlice'
import { useNavigate } from 'react-router-dom'
import HideAlbumPopup from './HideAlbumPopup'
import {
  deleteAlbumAPI,
  lockAlbum,
  toggleMediaPaymentAPI,
} from '../../../../Redux/ApiCalls/Dashboard/AlbumAPI'
import AllowedUserModal from './AllowedUserModal'
import PhotoPaymentModal from '../Album/PhotoPaymentModal'
import DeletePopup from '../Folder/DeletePopup'
import { IMAGE_CDN } from '../../../../config/config'
import { usePermissions } from '../../../../hooks/usePermissions'

interface CardProps {
  album: Albums
}

const MENU_WIDTH = 260
const MENU_MAX_HEIGHT = 400
const gradient =
  'linear-gradient(144.01deg, rgba(255, 201, 81, 0.9) 0%, rgba(206, 86, 176, 0.9) 100%)'

const AlbumCard: React.FC<CardProps> = ({ album }) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [isHideAlbumPopUp, setIsHideAlbumPopUp] = useState(false)
  const [isHiding, setIsHiding] = useState(false)
  const [showAllowedUserPopup, setShowAllowedUserPopup] = useState(false)
  const [photoPricingModal, setPhotoPricingModal] = useState(false)
  const [deleteAlbumPopup, setDeleteAlbumPopup] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { MediaPayment } = useAppSelector((state) => state.flags)
  const { hasPermission } = usePermissions()
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const canLock = hasPermission('albums.lock')
  const canPricing = Boolean(MediaPayment && hasPermission('albums.activate_media_pricing'))
  const canShareCode = hasPermission('albums.share_album_code')
  const canRedeemUsers = hasPermission('albums.redeem_code_users')
  const canHide = hasPermission('albums.hide')
  const canWatermark = hasPermission('albums.activate_watermark')
  const canDelete = hasPermission('albums.delete')

  const hasAnyAction =
    canLock ||
    canPricing ||
    canShareCode ||
    canRedeemUsers ||
    canHide ||
    canWatermark ||
    canDelete
  const hasPrecedingAction =
    canLock ||
    canPricing ||
    canShareCode ||
    canRedeemUsers ||
    canHide ||
    canWatermark

  const openMenu = (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen((prev) => !prev)
  }

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top

    let top =
      spaceBelow >= MENU_MAX_HEIGHT || spaceBelow >= spaceAbove
        ? rect.bottom + 8
        : rect.top - MENU_MAX_HEIGHT - 8

    top = Math.max(8, Math.min(top, window.innerHeight - MENU_MAX_HEIGHT - 8))

    let left = rect.right - MENU_WIDTH
    left = Math.max(8, Math.min(left, window.innerWidth - MENU_WIDTH - 8))

    setMenuPosition({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuPosition(null)
      return
    }
    updateMenuPosition()
    window.addEventListener('scroll', updateMenuPosition, true)
    window.addEventListener('resize', updateMenuPosition)
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true)
      window.removeEventListener('resize', updateMenuPosition)
    }
  }, [menuOpen, updateMenuPosition])

  const handleClickOutside = (event: MouseEvent) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setMenuOpen(false)
    }
  }

  useEffect(() => {
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen, showModal])

  const handleHideAlbum = async (id: number) => {
    if (isHiding) return
    const lock_type = album.is_hide ? 'unhide' : 'hide'
    setIsHiding(true)
    try {
      await dispatch(lockAlbum({ project_id: id, lock_type })).unwrap()
      setIsHideAlbumPopUp(false)
    } catch {
      // errors handled in reducer toast
    } finally {
      setIsHiding(false)
    }
  }
  const toggleWatermarkStatus = (id: number, status: boolean) => {
    if (status) {
      dispatch(lockAlbum({ project_id: id, lock_type: 'disable_watermark' }))
    } else {
      dispatch(lockAlbum({ project_id: id, lock_type: 'activate_watermark' }))
    }
  }

  const togglePaymentFlow = (id: number, active: boolean, price?: number) => {
    dispatch(
      toggleMediaPaymentAPI({
        purchased_image: active,
        per_image_price: price,
        project_id: id,
      })
    )
  }

  const handleDeleteAlbum = async () => {
    if (isDeleting) return
    setIsDeleting(true)
    try {
      await dispatch(deleteAlbumAPI({ album_id: album.id })).unwrap()
      setDeleteAlbumPopup(false)
    } catch {
      // errors handled in reducer toast
    } finally {
      setIsDeleting(false)
    }
  }

  const coverSrc = `${IMAGE_CDN}/${album.image}`

  return (
    <>
      {createPortal(
        <>
          {photoPricingModal && (
            <PhotoPaymentModal
              isOpen={photoPricingModal}
              isActive={album.purchased_image}
              initialprice={album.per_image_price ?? 0}
              projectId={album.id}
              onUpdate={(price, isActive) => {
                togglePaymentFlow(album.id, isActive, price)
                setPhotoPricingModal(false)
              }}
              onClose={() => setPhotoPricingModal(false)}
              onSubmit={(data) => {
                togglePaymentFlow(album.id, true, data)
                setPhotoPricingModal(false)
              }}
            />
          )}
          {isHideAlbumPopUp && (
            <HideAlbumPopup
              text={
                album.is_hide
                  ? 'Are you sure you want to unhide this album?'
                  : 'Are you sure you want to hide this album?'
              }
              buttonText={album.is_hide ? 'Unhide' : 'Hide'}
              isHidden={!!album.is_hide}
              isLoading={isHiding}
              cancel={() => {
                if (!isHiding) setIsHideAlbumPopUp(false)
              }}
              Hide={() => handleHideAlbum(album.id)}
            />
          )}
          {showModal && (
            <LockAlbumModal album={album} setShowModal={setShowModal} />
          )}
          {showAllowedUserPopup && (
            <AllowedUserModal
              album={album}
              isAlbum={true}
              onClose={() => setShowAllowedUserPopup(false)}
            />
          )}
          {deleteAlbumPopup && (
            <DeletePopup
              text="Are you sure you want to delete this entire album? This cannot be undone."
              buttonText="Delete"
              isDeleting={isDeleting}
              cancel={() => {
                if (!isDeleting) setDeleteAlbumPopup(false)
              }}
              Delete={handleDeleteAlbum}
            />
          )}
        </>,
        document.body
      )}

      {hasAnyAction &&
        menuOpen &&
        menuPosition &&
        createPortal(
          <DropdownMenu
            ref={menuRef}
            $top={menuPosition.top}
            $left={menuPosition.left}
            onClick={(e) => e.stopPropagation()}
          >
            {canLock && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  setShowModal(true)
                }}
              >
                <ItemIconContainer>
                  {album.is_locked ? <Unlock size={15} /> : <Lock size={15} />}
                </ItemIconContainer>
                <ItemName>
                  {album.is_locked ? 'Unlock Album' : 'Lock Album'}
                </ItemName>
              </MenuItem>
            )}

            {canPricing && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  setPhotoPricingModal(true)
                }}
              >
                <ItemIconContainer>
                  <Wallet size={15} />
                </ItemIconContainer>
                <ItemName>
                  {album.purchased_image ? 'Update' : 'Activate'} Media Pricing
                </ItemName>
              </MenuItem>
            )}

            {MediaPayment && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  dispatch(setCurrentAlbum(album))
                  navigate(`/dashboard/albums/image-sales/${album.id}`)
                }}
              >
                <ItemIconContainer>
                  <IndianRupee size={15} />
                </ItemIconContainer>
                <ItemName>Image Sales &amp; Commission</ItemName>
              </MenuItem>
            )}

            <MenuItem
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpen(false)
                dispatch(setCurrentAlbum(album))
                navigate(
                  `/dashboard/albums/share/${album.project_code}/${album.full_access_project_code}`
                )
              }}
            >
              <ItemIconContainer>
                <Share2 size={15} />
              </ItemIconContainer>
              <ItemName>Share Album Code</ItemName>
            </MenuItem>

            {canRedeemUsers && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  dispatch(setCurrentAlbum(album))
                  navigate(`/dashboard/albums/redeemUsers/${album.id}`)
                }}
              >
                <ItemIconContainer>
                  <Users size={15} />
                </ItemIconContainer>
                <ItemName>Redeem Code Users</ItemName>
              </MenuItem>
            )}

            {canHide && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  setIsHideAlbumPopUp(true)
                }}
              >
                <ItemIconContainer>
                  {album.is_hide ? <Eye size={15} /> : <EyeOff size={15} />}
                </ItemIconContainer>
                <ItemName>{album.is_hide ? 'Unhide Album' : 'Hide Album'}</ItemName>
              </MenuItem>
            )}

            {canWatermark && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  toggleWatermarkStatus(album.id, album.activate_watermark)
                }}
              >
                <ItemIconContainer>
                  <Droplets size={15} />
                </ItemIconContainer>
                <ItemName>
                  {album.activate_watermark
                    ? 'Remove Watermark'
                    : 'Activate Watermark'}
                </ItemName>
              </MenuItem>
            )}

            {canDelete && hasPrecedingAction && <MenuDivider />}

            {canDelete && (
              <MenuItem
                $danger
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  setDeleteAlbumPopup(true)
                }}
              >
                <ItemIconContainer $danger>
                  <Trash2 size={15} />
                </ItemIconContainer>
                <ItemName $danger>Delete Album</ItemName>
              </MenuItem>
            )}
          </DropdownMenu>,
          document.body
        )}

      <Card
        onClick={() => {
          if (menuOpen) return
          dispatch(setCurrentAlbum(album))
          navigate(`/dashboard/singleAlbum/${album.id}`)
        }}
      >
        <Cover $src={coverSrc}>
          <CoverScrim />
          <StatusRow>
            {album.is_locked ? (
              <StatusChip title="Locked">
                <Lock size={12} strokeWidth={2.2} />
                Locked
              </StatusChip>
            ) : null}
            {album.is_hide ? (
              <StatusChip title="Hidden">
                <EyeOff size={12} strokeWidth={2.2} />
                Hidden
              </StatusChip>
            ) : null}
            {album.activate_watermark ? (
              <StatusChip title="Watermark on">
                <Droplets size={12} strokeWidth={2.2} />
                Watermark
              </StatusChip>
            ) : null}
            {album.purchased_image ? (
              <StatusChip title="Media pricing active">
                <Wallet size={12} strokeWidth={2.2} />
                Paid
              </StatusChip>
            ) : null}
          </StatusRow>

          {hasAnyAction && (
            <MenuButton
              ref={buttonRef}
              type="button"
              onClick={openMenu}
              aria-label="Album options"
              aria-expanded={menuOpen}
            >
              <MoreVertical size={16} />
            </MenuButton>
          )}

          <CardMeta>
            <CardName>{album.name}</CardName>
            <CardDate>
              <CalendarDays size={13} strokeWidth={2} />
              {album.date || '—'}
            </CardDate>
          </CardMeta>
        </Cover>
      </Card>
    </>
  )
}

export default AlbumCard

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`

const Card = styled.article`
  position: relative;
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.07);
  box-shadow: 0 2px 14px rgba(23, 23, 23, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(201, 77, 170, 0.16);
    box-shadow: 0 12px 28px rgba(23, 23, 23, 0.1);
  }
`

const Cover = styled.div<{ $src: string }>`
  position: relative;
  width: 100%;
  height: 280px;
  background-image: url(${(p) => p.$src});
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const CoverScrim = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(15, 15, 20, 0.18) 0%,
    rgba(15, 15, 20, 0.05) 45%,
    rgba(15, 15, 20, 0.55) 82%,
    rgba(15, 15, 20, 0.82) 100%
  );
`

const StatusRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 14px 14px 0;
  padding-right: 56px;
`

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 9px;
  border-radius: 999px;
  font-family: Urbanist, sans-serif;
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.01em;
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
`

const MenuButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.55);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.28);
    background: #fff;
  }
`

const CardMeta = styled.div`
  position: relative;
  z-index: 1;
  padding: 18px 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const CardName = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.25;
  color: #fff;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.35);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const CardDate = styled.p`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.88);
`

const DropdownMenu = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: ${MENU_WIDTH}px;
  max-height: ${MENU_MAX_HEIGHT}px;
  overflow-y: auto;
  z-index: 10050;
  background: #fff;
  border-radius: 14px;
  padding: 6px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  box-shadow: 0 14px 40px rgba(23, 23, 23, 0.14);
  animation: ${fadeIn} 0.15s ease-out;
`

const MenuItem = styled.button<{ $danger?: boolean }>`
  width: 100%;
  border: none;
  background: transparent;
  padding: 10px 10px;
  border-radius: 10px;
  cursor: pointer;
  color: ${(p) => (p.$danger ? '#e44862' : '#171717')};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) =>
    p.$danger ? 'rgba(228, 72, 98, 0.08)' : 'rgba(201, 77, 170, 0.06)'};
  }
`

const MenuDivider = styled.div`
  height: 1px;
  margin: 4px 8px;
  background: rgba(23, 23, 23, 0.08);
`

const ItemIconContainer = styled.div<{ $danger?: boolean }>`
  flex-shrink: 0;
  border-radius: 10px;
  background: ${(p) => (p.$danger ? '#e44862' : gradient)};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
`

const ItemName = styled.span<{ $danger?: boolean }>`
  font-family: Urbanist, sans-serif;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.3;
  text-align: left;
  color: ${(p) => (p.$danger ? '#e44862' : '#171717')};
`
